import {
	type Money,
	addMoney,
	averageMoney,
	moneyFromMajorFloat,
	moneyFromWire,
	sumMoney,
	toMajorFloatAmount,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";
import type { BudgetReviewsResponse } from "@performyard/service-contracts";

import type { DecisionRow, DecisionRowApprovalSnapshot } from "../../types";
import type { GroupBudgetFigures, IncreaseValues } from "../MyDecisions/types";

import {
	isOverBudgetMoney,
	percentAllocated,
	remainingBudget,
	resolveGroupBudget,
	sumAmounts,
} from "./money-aggregation";

export const pluralize = (count: number, singular: string, plural: string): string => (count === 1 ? singular : plural);

type ManagerGroup = BudgetReviewsResponse["individualBudgets"][string];

export type ManagerBudgetGroupWithCycleAllotment = ManagerGroup & {
	allotmentIncrease: number;
	allotmentIncreasePercent: number | null;
};

const attachCycleAllotment = (
	group: ManagerGroup,
	allotmentIncrease: number,
	allotmentIncreasePercent: number | null
): ManagerBudgetGroupWithCycleAllotment => ({
	...group,
	allotmentIncrease,
	allotmentIncreasePercent,
});

export const budgetGroupKey = (group: ManagerGroup): string =>
	group.overrideManagerId ? `${group.managerId}:${group.overrideManagerId}` : group.managerId;

/** Keeps the first group per line manager, or per manager + override pair when an override exists. */
export const dedupeBudgetGroupsByManagerId = (groups: ManagerGroup[]): ManagerGroup[] => {
	const byKey = new Map<string, ManagerGroup>();
	for (const group of groups) {
		const key = budgetGroupKey(group);
		if (!byKey.has(key)) {
			byKey.set(key, group);
		}
	}
	return [...byKey.values()];
};

const hasResults = (g: ManagerGroup): boolean => g.results.length > 0;

const isApprovedManagerGroup = (group: ManagerGroup): boolean => hasResults(group) && group?.status === "approved";

const isInReviewManagerGroup = (group: ManagerGroup): boolean => hasResults(group) && group?.status === "in_review";

/**
 * Whether a manager has allocated more than their share of the cycle budget.
 *
 * This is a decision, not a display value, so it is made on exact money rather
 * than on two separately-rounded floats. Any amount over the budget counts,
 * including a single cent — the budget shown to the manager is exact, so one
 * cent over is one cent they allocated.
 */
const isOverBudget = (group: ManagerGroup, cycleAllotmentIncreasePercent: number): boolean => {
	if (!hasResults(group)) return false;
	const totalProposedIncrease = sumAmounts(group.results.map((r) => r.proposedIncreaseAmount));
	const totalCurrentSalary = sumAmounts(group.results.map((r) => r.currentSalary));
	const budget = resolveGroupBudget(group.groupIncreaseBudget, totalCurrentSalary, cycleAllotmentIncreasePercent);
	return isOverBudgetMoney(totalProposedIncrease, budget);
};
/**
 * Median of a set of money amounts.
 *
 * An earlier comment justified plain numbers here on the grounds that "a median picks
 * an existing row's value rather than combining rows, so no error accumulates." That
 * holds only for an odd count. For an even count the two middle values are averaged,
 * which is combining — so the even branch goes through `averageMoney`, which splits
 * without losing or inventing a minor unit. Sorting is on exact minor units.
 */
const calculateMedian = (values: Money[]): Money | undefined => {
	if (values.length === 0) return undefined;
	const sorted = [...values].sort((a, b) => toMinorUnits(a) - toMinorUnits(b));
	const mid = Math.floor(sorted.length / 2);
	if (sorted.length % 2 !== 0) return sorted[mid]!;
	return averageMoney(addMoney(sorted[mid - 1]!, sorted[mid]!), 2);
};

export const parseBudgetReviewsData = (cycleBudgetData: BudgetReviewsResponse | undefined) => {
	const cycleData = cycleBudgetData?.compensationCycleData;
	const individualBudgets = cycleBudgetData?.individualBudgets ?? {};
	const cycleAllotmentIncreaseMoney = moneyFromWire(cycleData?.allotmentIncrease) ?? zeroMoney();
	// `attachCycleAllotment` threads this through the manager-group shape, which is still
	// numeric; the exact value is what leaves this function as `cycleBudget`.
	const cycleAllotmentIncrease = toMajorFloatAmount(cycleAllotmentIncreaseMoney);
	const cycleAllotmentIncreasePercent = cycleData?.allotmentIncreasePercent ?? null;
	const managerGroups = dedupeBudgetGroupsByManagerId(Object.values(individualBudgets)).map((group) =>
		attachCycleAllotment(group, cycleAllotmentIncrease, cycleAllotmentIncreasePercent)
	);
	const approvedBudgets = managerGroups.filter(isApprovedManagerGroup);
	const submittedBudgets = managerGroups.filter(isInReviewManagerGroup);
	const notSubmittedBudgets = managerGroups.filter(
		(g) => hasResults(g) && !isApprovedManagerGroup(g) && !isInReviewManagerGroup(g)
	);
	const approvedBudgetCount = approvedBudgets.length;
	const submittedBudgetCount = submittedBudgets.length;
	const notSubmittedBudgetCount = notSubmittedBudgets.length;

	const budgetCount = managerGroups.length;
	const approvedBudgetsPercent = budgetCount === 0 ? "0" : ((approvedBudgetCount / budgetCount) * 100).toFixed(0);
	const notApprovedBudgetsPercent =
		budgetCount === 0 ? "0" : (((notSubmittedBudgetCount + submittedBudgetCount) / budgetCount) * 100).toFixed(0);

	// Summed exactly: these run across every row in the cycle, which is where
	// per-row float error compounds into a visibly wrong total. They stay `Money` all
	// the way to the widgets — flattening here is what let the consumers subtract and
	// divide them as floats.
	const approvedSpend = sumAmounts(
		approvedBudgets.flatMap((group) => group.results.map((row) => row.proposedIncreaseAmount))
	);
	const approvedIncreasesArray = approvedBudgets.flatMap((budget) =>
		budget.results.map((result) => moneyFromWire(result.proposedIncreaseAmount) ?? zeroMoney())
	);
	const approvedIncreasePercentsArray = approvedBudgets.flatMap((budget) =>
		budget.results.map((result) => result.proposedIncreasePercent ?? 0).sort((a, b) => a - b)
	);

	const employeesApproved = approvedBudgets.reduce((total, group) => total + group.results.length, 0);
	const submittedSpend = sumAmounts(
		submittedBudgets.flatMap((group) => group.results.map((row) => row.proposedIncreaseAmount))
	);
	const employeesSubmitted = submittedBudgets.reduce((total, group) => total + group.results.length, 0);
	const notSubmittedEmployeeCount = notSubmittedBudgets.reduce((total, group) => total + group.results.length, 0);
	const overBudgetBudgets =
		cycleAllotmentIncreasePercent != null
			? managerGroups.filter((g) => isOverBudget(g, cycleAllotmentIncreasePercent))
			: [];

	const approvedPercentSum = approvedIncreasePercentsArray.reduce((sum, p) => sum + p, 0);
	const averageApprovedPercentIncrease = employeesApproved === 0 ? 0 : approvedPercentSum / employeesApproved;

	const medianApprovedIncreasePerEmployee = calculateMedian(approvedIncreasesArray) ?? zeroMoney();
	const notApprovedSpend = sumAmounts(
		[...notSubmittedBudgets, ...submittedBudgets].flatMap((group) =>
			group.results.map((row) => row.proposedIncreaseAmount)
		)
	);
	return {
		cycleBudget: cycleAllotmentIncreaseMoney,
		approvedSpend,
		approvedIncreasePercentsArray,
		averageApprovedPercentIncrease,
		medianApprovedIncreasePerEmployee,
		submittedSpend,
		employeesApproved,
		employeesSubmitted,
		notSubmittedEmployeeCount,
		employeeCount: cycleData?.budgetSummary?.employeeCount ?? 0,
		percentIncrease: cycleData?.allotmentIncreasePercent ?? null,
		individualBudgets,
		managerGroups,
		budgetCount,
		approvedBudgetCount,
		submittedBudgetCount,
		notSubmittedBudgetCount,
		approvedBudgetsPercent,
		notApprovedBudgetsPercent,
		approvedBudgets,
		submittedBudgets,
		notSubmittedBudgets,
		overBudgetBudgets,
		overBudgetBudgetCount: overBudgetBudgets.length ?? 0,
		notApprovedSpend,
	};
};

type BudgetResultRow = ManagerBudgetGroupWithCycleAllotment["results"][number];

const mapApprovalToSnapshot = (a: NonNullable<BudgetResultRow["approvals"]>[number]): DecisionRowApprovalSnapshot => ({
	approver_id: String(a.approverId),
	approver_name: a.approverName,
	sequence: a.sequence,
	status: a.status,
	decided_at: a.decidedAt != null ? String(a.decidedAt) : null,
	notes: a.notes ?? null,
	amount_at_approval: moneyFromWire(a.amountAtApproval),
	updated_at: a.updatedAt != null ? String(a.updatedAt) : undefined,
	proposed_increase_amount: moneyFromWire(a.proposedIncreaseAmount),
	proposed_increase_percent: a.proposedIncreasePercent ?? null,
	proposed_salary: moneyFromWire(a.proposedSalary),
});

export const budgetReviewResultToDecisionRow = (r: BudgetResultRow): DecisionRow => {
	const approvalEntries = (r.approvals ?? []).map(mapApprovalToSnapshot);
	const submittedApproval = [...approvalEntries].reverse().find((e) => e.status === "submitted");
	const fallbackApprover = approvalEntries[0];
	const submitApprover = submittedApproval ?? fallbackApprover;
	const notYetSubmitted = r.status === "pending";
	return {
		_id: String(r.id),
		compensation_cycle_id: String(r.compensationCycle),
		employee_id: String(r.employeeId),
		employee_name: r.employeeName,
		employee_title: "",
		// Budget-review rows do not carry a role; the grid renders a blank cell.
		employee_role: null,
		current_salary: moneyFromWire(r.currentSalary) ?? zeroMoney(),
		proposed_increase_percent: notYetSubmitted ? null : r.proposedIncreasePercent,
		proposed_increase_amount: notYetSubmitted ? null : moneyFromWire(r.proposedIncreaseAmount),
		proposed_salary: notYetSubmitted ? null : moneyFromWire(r.proposedSalary),
		status: r.status,
		notes: r.myDecisionNotes ?? null,
		approver: submitApprover?.approver_id != null ? String(submitApprover.approver_id) : null,
		updatedAt: r.updatedAt != null ? String(r.updatedAt) : null,
		approval_entries: approvalEntries.length > 0 ? approvalEntries : undefined,
	};
};

export const computeAdjustedBudgetFigures = (
	baseRows: DecisionRow[],
	draftOverrides: IncreaseValues,
	groupIncreaseBudget: Money
): GroupBudgetFigures => {
	// A manager's in-progress edits, totalled exactly. The 0.01 epsilons this
	// replaces were hand-rolled tolerances for float drift; the money helpers
	// carry that tolerance once, in one place.
	const allocated = sumMoney(
		baseRows.map((row) => {
			const draft = draftOverrides[row._id];
			// A draft is a user-typed major-unit number; a saved row already holds Money.
			if (draft && typeof draft.amount === "number") return moneyFromMajorFloat(draft.amount);
			return row.status !== "pending" ? (row.proposed_increase_amount ?? zeroMoney()) : zeroMoney();
		})
	);
	return {
		groupIncreaseBudget,
		groupAllocated: allocated,
		groupPercentAllocated: percentAllocated(allocated, groupIncreaseBudget),
		groupRemaining: remainingBudget(groupIncreaseBudget, allocated),
		isGroupOverBudget: isOverBudgetMoney(allocated, groupIncreaseBudget),
	};
};

export const mapBudgetGroupResultsToDecisionRows = (
	results: ManagerBudgetGroupWithCycleAllotment["results"]
): DecisionRow[] => results.map((row) => budgetReviewResultToDecisionRow(row));
